import { useSeriesAvailabilityQuery } from '../../../../lib/SharedQueries.js';
import Loading from '../../../Loading';
import {
    formatDateLong,
    formatDateTime,
    formatDateTimeLong,
    formatTime,
} from '../../../../lib/DateTime.js';
import { useEffect } from 'react';
import SeriesTime from '~/components/forms/questions/scheduling/SeriesTime';
import { InformationCircleIcon } from '@heroicons/react/24/outline';

function getValueFromSeries(series) {
    return series.map(instance => instance.start);
}

export const InProgressBanner = ({remaining, total}) => {
    return (
        <div className="mb-2 mt-2 themed-bg-light themed-text px-4 py-3 rounded-lg flex items-center space-x-2">
            <div class="w-5 h-5"><InformationCircleIcon className="w-5 h-5" /></div>
            <div class="leading-tight">This is in progress. There are {remaining} of {total} times remaining.</div>
        </div>
    );
}

const SeriesRow = ({
    series,
    onClick,
    isSelected,
    hideSpotsLeft,
    className,
    quantity,
    duration,
    clientTz,
}) => {
    const times = series.map(instance => (
        <SeriesTime
            key={instance.id}
            start={instance.start}
            duration={duration}
            clientTz={clientTz}
        />
    ));
    const spotsLeft = series[0].spots_left;
    const inProgressSeries = !!series[0].in_progress;
    const label = series[0].settings?.label || '';
    const isDisabled = spotsLeft < (quantity || 1);

    return (
        <div className={`border ${isSelected ? 'border-primary-700 themed-border' : ''} rounded-lg p-6  ${className}`}>
            {inProgressSeries && (
                <InProgressBanner
                    remaining={series[0].in_progress_remaining}
                    total={series[0].in_progress_total}
                />
            )}

            <label
                className={`flex items-start space-x-4 cursor-pointer`}
                onClick={() => !isDisabled && onClick()}
            >
                <input
                    type="radio"
                    checked={isSelected}
                    className="mt-1"
                    disabled={isDisabled}
                />
                <div className="flex flex-wrap items-center justify-between flex-1">
                    <div>
                        {!!label && (
                            <div className="mb-2 font-semibold">{label}</div>
                        )}
                        {times.map(t => t)}
                    </div>
                    {!hideSpotsLeft && (
                        <div
                            className={`mt-1 text-gray-500 ${isDisabled ? 'text-red-600' : ''}`}
                        >
                            {spotsLeft} spot{spotsLeft !== 1 ? 's' : ''} remaining
                        </div>
                    )}
                    {isDisabled && hideSpotsLeft && (
                        <div className="mt-1 text-red-600">
                            There are no spots remaining
                        </div>
                    )}
                </div>
            </label>
        </div>
    );
};

const SeriesPicker = ({
    value,
    onChange,
    settings,
    quantity,
    bookableResourceId,
    eventType,
    clientTz,
}) => {
    const { data, isLoading } = useSeriesAvailabilityQuery(
        eventType?.id,
        bookableResourceId,
        clientTz,
    );
    const seriesGroups = data || [];

    function isSame(item, value) {
        if (!item || !value || item?.length !== value?.length) {
            return false;
        }

        return (
            JSON.stringify(getValueFromSeries(item)) === JSON.stringify(value)
        );
    }

    useEffect(() => {
        if (
            !isLoading &&
            !value &&
            seriesGroups.length === 1 &&
            seriesGroups[0][0]?.spotsLeft > 0
        ) {
            // Automatically pick
            setTimeout(() => {
                onChange(getValueFromSeries(seriesGroups[0]));
            }, 5);
        }
    }, [isLoading, value, seriesGroups.length]);

    return (
        <div>
            {isLoading && <Loading />}
            {seriesGroups.map((seriesGroup, idx) => (
                <SeriesRow
                    series={seriesGroup}
                    isSelected={isSame(seriesGroup, value)}
                    onClick={() => onChange(getValueFromSeries(seriesGroup))}
                    key={idx}
                    hideSpotsLeft={settings?.hideSpotsLeft}
                    className={'mt-3'}
                    quantity={quantity}
                    duration={eventType?.duration}
                    clientTz={clientTz}
                />
            ))}
            {!isLoading && !seriesGroups.length && (
                <div>There are no upcoming times available</div>
            )}
        </div>
    );
};

export default SeriesPicker;
